import { DatePipe } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { Audit } from 'src/app/models/audit.model';
import { DialogService } from 'src/app/services/modal/dialog.service';

interface AuditDisplayRow {
  field: string
  oldValue: string
  newValue: string
}

interface AuditDisplayGroup {
  title: string
  titlePrefix?: string
  guardType: string | null
  removed: boolean
  added: boolean
  rows: AuditDisplayRow[]
}

interface AuditDisplaySection {
  title: string
  groups: AuditDisplayGroup[]
}

@Component({
  standalone: false,
  selector: 'app-audit-modal',
  templateUrl: './audit-modal.component.html',
  styleUrls: ['./audit-modal.component.css']
})
export class AuditModalComponent implements OnChanges {

  @Input() values: any = []
  auditSections: AuditDisplaySection[] = []
  private prevState = new Map<string, string>()
  private currentState = new Map<string, string>()

  constructor(public dialogService: DialogService, private datePipe: DatePipe) {}

  ngOnChanges(): void {
    this.prevState = this.buildStateMap(this.data?.prevStateObj)
    this.currentState = this.buildStateMap(this.data?.currentStateObj)
    this.auditSections = this.buildAuditSections(this.values ?? [])
  }

  stringToDate(date: string) {
    const newDate = new Date(date);
    const customFormat = 'dd-MMM-yyyy h:mma';
    const formattedDate = this.datePipe.transform(newDate, customFormat);
    return formattedDate;
  }

  @Input() data: Audit = {
    eventId: 0,
    eventTime: '',
    eventDetails: '',
    performedBy: '',
    applicationSource: '',
    userType: '',
    performedOn: '',
    prevStateObj: {},
    currentStateObj: {},
    association: ''
  }
  @Output() onCancel: EventEmitter<any> = new EventEmitter<any>()

  get formattedAction(): string {
    const details = this.data?.eventDetails ?? ''
    const name = this.data?.performedOn
    if (!name) return details
    return details.replace(/^(Logsheet|Log Sheet|Form|Standard Form)(\s+)/i, `$1$2"${name}" `)
  }

  toggleModal() {
    this.onCancel.emit()
  }

  getTenantInfo(): string {
    if (this.data.association) {
      const parts = this.data.association.split(' > ');
      if (parts.length >= 3) {
        return `${parts[0]}, ${parts[1]}, ${parts[2]}`;
      }
    }
    return '';
  }

  private buildStateMap(stateObj: any): Map<string, string> {
    const map = new Map<string, string>()
    if (!stateObj) return map

    if (Array.isArray(stateObj)) {
      for (const entry of stateObj) {
        const key = entry?.key ?? entry?.Key
        if (typeof key !== 'string') continue
        map.set(key, this.normalizeValue(entry?.value ?? entry?.Value))
      }
    } else if (typeof stateObj === 'object') {
      for (const [key, value] of Object.entries(stateObj)) {
        map.set(key, this.normalizeValue(value))
      }
    }

    return map
  }

  private normalizeValue(value: any): string {
    if (value === null || value === undefined || value === '') return '-'
    return String(value)
  }

  private normalizeFieldName(value: string, section = ''): string {
    const guardNames: Record<string, string> = {
      'Guard name': 'Guard label',
      'Field checked': 'Source field',
      'Rule': 'Function',
      'Conditional Rule': 'Function',
      'Value to check': 'Conditional Value',
      'Condition value': 'Conditional Value',
      'Guard behavior': 'Submission guard type',
      'Run order': 'Priority',
      'Message shown': 'Warning message',
    }

    const layoutNames: Record<string, string> = {
      'Field order': 'Position',
      'Starts hidden': 'Hidden by default',
      'Depends on': 'Source field',
      'Rule': 'Function',
      'Conditional Rule': 'Function',
      'Value to check': 'Conditional Value',
      'Condition value': 'Conditional Value',
      'Required once shown': 'Required when visible',
      'Always required': 'Mandatory',
    }

    if (section === 'Submission Guards') return guardNames[value] ?? layoutNames[value] ?? value
    return layoutNames[value] ?? guardNames[value] ?? value
  }

  private parseLegacyKey(key: string) {
    const guardMatch = key.match(/^Submission Guard\s+\d+\s+(.+)$/i)
    if (guardMatch) {
      return {
        section: 'Submission Guards',
        group: 'Submission Guard',
        field: this.normalizeFieldName(guardMatch[1], 'Submission Guards'),
      }
    }

    const layoutSuffixes = [
      'Hidden by default', 'Starts hidden',
      'Source field', 'Depends on',
      'Function', 'Rule', 'Conditional Rule',
      'Conditional Value', 'Condition value', 'Value to check',
      'Required when visible', 'Required once shown',
      'Mandatory', 'Always required',
      'Position', 'Field order',
    ]
    const matchedSuffix = layoutSuffixes.find(suffix => key.endsWith(` ${suffix}`))
    if (matchedSuffix) {
      return {
        section: 'Layout & Configuration',
        group: key.slice(0, -matchedSuffix.length).trim(),
        field: this.normalizeFieldName(matchedSuffix, 'Layout & Configuration'),
      }
    }

    return {
      section: 'Changes',
      group: '',
      field: this.normalizeFieldName(key),
    }
  }

  private formatStateValue(field: string, value: any): string {
    const normalized = this.normalizeValue(value)
    if (normalized === '-') return normalized

    const toggleFields = ['Hidden by default', 'Required when visible', 'Mandatory']
    if (!toggleFields.includes(field)) return normalized

    const labelled = normalized.match(/^(.+?)\s*-\s*(On|Off)$/i)
    if (labelled) {
      const state = labelled[2].toLowerCase() === 'on' ? 'On' : 'Off'
      return `${field} - ${state}`
    }

    const lower = normalized.toLowerCase()
    if (lower === 'on') return `${field} - On`
    if (lower === 'off') return `${field} - Off`
    if (field === 'Mandatory') {
      if (lower === 'mandatory') return 'Mandatory - On'
      if (lower === 'optional') return 'Mandatory - Off'
    }

    return normalized
  }

  private normalizeRow(item: any) {
    const rawKey = this.normalizeValue(item?.key ?? item?.Key)
    const keyParts = rawKey.split('|').map(part => part.trim()).filter(Boolean)

    if (keyParts.length >= 3) {
      const sectionName = keyParts[0]
      const field = this.normalizeFieldName(keyParts.slice(2).join(' - '), sectionName)
      return {
        section: sectionName,
        group: keyParts[1],
        field,
        oldValue: this.formatStateValue(field, item?.oldValue),
        newValue: this.formatStateValue(field, item?.newValue),
      }
    }

    if (keyParts.length === 2) {
      const sectionName = keyParts[0]
      const field = this.normalizeFieldName(keyParts[1], sectionName)
      return {
        section: sectionName,
        group: '',
        field,
        oldValue: this.formatStateValue(field, item?.oldValue),
        newValue: this.formatStateValue(field, item?.newValue),
      }
    }

    const parsed = this.parseLegacyKey(rawKey)
    return {
      ...parsed,
      oldValue: this.formatStateValue(parsed.field, item?.oldValue),
      newValue: this.formatStateValue(parsed.field, item?.newValue),
    }
  }

  private lookupState(guardKey: string, ...fieldNames: string[]): string {
    for (const field of fieldNames) {
      const key = `Submission Guards|${guardKey}|${field}`
      const current = this.currentState.get(key)
      if (current && current !== '-') return current
      const previous = this.prevState.get(key)
      if (previous && previous !== '-') return previous
    }
    return ''
  }

  private buildGuardGroup(guardKey: string, rows: AuditDisplayRow[]): AuditDisplayGroup {
    const label = this.lookupState(guardKey, 'Guard label', 'Guard name') || guardKey
    const guardType = this.lookupState(guardKey, 'Submission guard type', 'Guard behavior') || null
    const removed = rows.every(r => r.newValue === '-')
    const added = !removed && rows.every(r => r.oldValue === '-')

    return {
      title: label,
      titlePrefix: 'Guard name',
      guardType,
      removed,
      added,
      rows,
    }
  }

  private buildAuditSections(values: any[]): AuditDisplaySection[] {
    const normalizedRows = values.map(value => this.normalizeRow(value))
    const sectionMap = new Map<string, Map<string, AuditDisplayRow[]>>()

    for (const row of normalizedRows) {
      const sectionTitle = row.section || 'Changes'
      const groupTitle = row.group || ''

      if (!sectionMap.has(sectionTitle)) {
        sectionMap.set(sectionTitle, new Map<string, AuditDisplayRow[]>())
      }

      const groupMap = sectionMap.get(sectionTitle)!
      if (!groupMap.has(groupTitle)) {
        groupMap.set(groupTitle, [])
      }

      groupMap.get(groupTitle)!.push({ field: row.field, oldValue: row.oldValue, newValue: row.newValue })
    }

    return Array.from(sectionMap.entries()).map(([sectionTitle, groups]) => ({
      title: sectionTitle,
      groups: Array.from(groups.entries()).map(([groupKey, rows]) => {
        if (sectionTitle === 'Submission Guards' && groupKey) {
          return this.buildGuardGroup(groupKey, rows)
        }
        const isLayout = sectionTitle === 'Layout & Configuration' && groupKey
        const removed = rows.every(r => r.newValue === '-')
        const added = !removed && rows.every(r => r.oldValue === '-')
        return {
          title: groupKey,
          titlePrefix: isLayout ? 'Form Field Name' : undefined,
          guardType: null,
          removed,
          added,
          rows,
        }
      }),
    }))
  }
}
